import cx from 'classnames';
import { useEffect } from 'react';
import {
  useDispatch,
  useSelector,
  type TypedUseSelectorHook,
} from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';

import config from '@plone/volto/registry';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

import styles from '../styles.module.css';
import { getQueryStringResults } from '@plone/volto/actions/querystringsearch/querystringsearch';
import type { QubicaBaseViewProps } from '../QubicaBaseView';
import type { Request } from '@redturtle/qubicaamf-theme/types';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import BuildingSVG from '@redturtle/qubicaamf-theme/components/Icons/BuildingSVG';
interface RootState {
  querystringsearch: Request & {
    subrequests: { hq: Request; offices: Request };
  };
}

const QubicaBaseFooter = (props: QubicaBaseViewProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const useTypedSelector: TypedUseSelectorHook<RootState> =
    useSelector<RootState>;

  const Container = config.getComponent('Container').component || 'div';
  const Image = config.getComponent('Image').component;

  const path = flattenToAppURL('/');

  const hqSubreqID = 'hq';
  const officesSubreqID = 'offices';

  useEffect(() => {
    dispatch(
      getQueryStringResults(
        path,
        {
          query: [
            {
              i: 'base_type',
              o: 'plone.app.querystring.operation.selection.any',
              v: 'main-headquarter',
            },
          ],
          metadata_fields: '_all',
          fullobjects: 1,
        },
        hqSubreqID,
        1,
      ),
    );
  }, [path, dispatch, hqSubreqID]);

  useEffect(() => {
    dispatch(
      getQueryStringResults(
        path,
        {
          query: [
            {
              i: 'base_type',
              o: 'plone.app.querystring.operation.selection.any',
              v: 'headquarter',
            },
          ],
          metadata_fields: '_all',
          fullobjects: 1,
        },
        officesSubreqID,
        1,
      ),
    );
  }, [path, dispatch, officesSubreqID]);

  const headquarters = useTypedSelector(
    (state) => state.querystringsearch.subrequests.hq,
  );

  const offices = useTypedSelector(
    (state) => state.querystringsearch.subrequests.offices,
  );

  return (
    <div className={cx('has--fullwidth--true', styles.qubicaBaseFooter)}>
      <Container>
        <div className={styles.hqTitleContainer}>
          <h2 className={styles.hqTitle}>
            {intl.formatMessage(messages.headquarters)}
          </h2>
        </div>
        <div className={styles.footerCardContainer}>
          {headquarters &&
            headquarters?.items.map((hq) => (
              <div key={hq['@id']} className={styles.card}>
                <div className={styles.hqCardImageContainer}>
                  {hq.image ? (
                    <Image
                      item={hq}
                      className={styles.hqCardImage}
                      alt={hq.image_caption ?? ''}
                    />
                  ) : (
                    <div className={styles.noImage}>
                      <BuildingSVG />
                    </div>
                  )}
                </div>
                <div className={styles.cardText}>
                  <div className={styles.hqCardTitleContainer}>
                    <h3>
                      <UniversalLink
                        href={hq['@id']}
                        openLinkInNewTab={true}
                        className={styles.hqCardTitle}
                      >
                        {hq.title}
                      </UniversalLink>
                    </h3>
                  </div>

                  {hq.address && (
                    <div className={styles.hqCardDescription}>{hq.address}</div>
                  )}
                  {hq.city && (
                    <div className={styles.hqCardDescription}>{hq.city}</div>
                  )}
                  {hq.state && (
                    <div className={styles.hqCardDescription}>{hq.state}</div>
                  )}

                  <div className={styles.CTAContainer}>
                    <UniversalLink
                      href={hq['@id']}
                      openLinkInNewTab={true}
                      className={styles.cta}
                    >
                      {intl.formatMessage(messages.view_cta)}
                    </UniversalLink>
                  </div>
                </div>
              </div>
            ))}
        </div>
        <div className={styles.regionalOfficesSection}>
          <div className={styles.regionalOfficesTitle}>
            <h3>{intl.formatMessage(messages.regional_offices)}</h3>
          </div>

          <div className={styles.footerCardContainer}>
            {offices &&
              offices.items.map((off) => (
                <div
                  className={cx(styles.card, styles.officeCard)}
                  key={off['@id']}
                >
                  <div className={styles.officeCardImageContainer}>
                    {off.image ? (
                      <Image
                        item={off}
                        className={styles.officeCardImage}
                        alt={off.image_caption ?? ''}
                      />
                    ) : (
                      <div className={styles.offNoImage}>
                        <BuildingSVG />
                      </div>
                    )}
                  </div>

                  <div className={styles.cardText}>
                    <div className={styles.officeCardTitleContainer}>
                      <h4>
                        <UniversalLink
                          href={off['@id']}
                          openLinkInNewTab={true}
                          className={styles.officeCardTitle}
                        >
                          {off.title}
                        </UniversalLink>
                      </h4>
                    </div>

                    {off.address && (
                      <div className={styles.officeCardDescription}>
                        {off.address}
                      </div>
                    )}
                    {off.city && (
                      <div className={styles.officeCardDescription}>
                        {off.city}
                      </div>
                    )}
                    {off.state && (
                      <div className={styles.officeCardDescription}>
                        {off.state}
                      </div>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default QubicaBaseFooter;

const messages = defineMessages({
  headquarters: {
    id: 'headquarters',
    defaultMessage: 'Headquarters',
  },
  regional_offices: {
    id: 'regional_offices',
    defaultMessage: 'Regional offices',
  },
  view_cta: {
    id: 'view_cta',
    defaultMessage: 'View',
  },
});
